import { useRef, useState } from 'react'
import { Alert, Spinner } from '@/components'
import { Button } from '@/components/atoms'
import { useGlobalModalStore } from '@/stores/useGlobalModalStore/useGlobalModalStore'

type EmailExportContentProps = {
    labels: {
        saveButton: string
        cancelButton?: string
    }
    list?: {
        icon?: React.ReactNode
        text: React.ReactNode
        textClassName?: string
    }[]
    title?: string
    handleOnSuccess?: () => void
    handleOnCancel?: () => void
    isLoading?: boolean
    /** When provided, submit runs this async handler first; on success calls handleOnSuccess. On rejection calls onSubmitError and keeps modal open. */
    onSubmit?: () => Promise<void>
    onSubmitError?: (error: unknown) => void
}

export const EmailExportContent: React.FC<EmailExportContentProps> = ({
    labels,
    list,
    title,
    isLoading: isLoadingProp,
    handleOnSuccess,
    handleOnCancel,
    onSubmit: onSubmitAsync,
    onSubmitError,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const isSubmittingRef = useRef(false)
    const setModalState = useGlobalModalStore((state) => state.setModalState)

    const closeModal = () => {
        setModalState({ isOpen: false })
    }

    const onCancelClick = () => {
        if (handleOnCancel) {
            handleOnCancel()
        } else {
            closeModal()
        }
    }

    const submitText = labels.saveButton
    const dismissText = labels.cancelButton
    const isLoading = isLoadingProp ?? isSubmitting

    const onSubmit = async () => {
        if (!onSubmitAsync) {
            handleOnSuccess?.()
            return
        }
        if (isSubmittingRef.current) return
        isSubmittingRef.current = true
        setIsSubmitting(true)
        try {
            await onSubmitAsync()
            handleOnSuccess?.()
        } catch (error) {
            onSubmitError?.(error)
        } finally {
            isSubmittingRef.current = false
            setIsSubmitting(false)
        }
    }

    return (
        <div className="text-sm">
            <div className="space-y-6 px-6 pb-6">
                <Alert
                    iconHidden
                    variant="info"
                    title={title}
                    description={
                        list &&
                        list.length > 0 && (
                            <ul
                                className="flex flex-col gap-2"
                                data-testid="content-text-list"
                            >
                                {list.map((item, idx) => (
                                    <li
                                        key={idx}
                                        className="text-body-medium flex items-start gap-2"
                                        data-testid={`content-text-list-item-${String(idx)}`}
                                    >
                                        {item.icon && (
                                            <span className="flex-shrink-0 text-blue-600">
                                                {item.icon}
                                            </span>
                                        )}
                                        <span className={item.textClassName}>
                                            {item.text}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )
                    }
                />
            </div>
            <div className="flex flex-row-reverse gap-4 border-t border-t-neutral-200 px-6 py-4">
                <Button
                    name="submit"
                    variant={'solidPrimary'}
                    size="large"
                    onClick={() => {
                        void onSubmit()
                    }}
                    disabled={isLoading}
                    className="whitespace-nowrap"
                >
                    {submitText}
                    {isLoading && (
                        <span data-testid="loading-state">
                            <Spinner size={16} width={3} />
                        </span>
                    )}
                </Button>
                {dismissText && (
                    <Button
                        name="cancel"
                        variant="ghostSecondary"
                        size="large"
                        onClick={onCancelClick}
                        className="whitespace-nowrap"
                    >
                        {dismissText}
                    </Button>
                )}
            </div>
        </div>
    )
}
